import { SEO } from "@/components/SEO";
import { PageHero } from "@/components/sections/PageHero";
import { IMG } from "@/lib/content";
import { Reveal, MaskedLines, ImageReveal } from "@/components/motion/Reveal";
import { Button } from "@/components/motion/Button";
import { Process } from "@/components/sections/Process";
import { Marquee } from "@/components/sections/Marquee";
import { FAQ } from "@/components/sections/FAQ";
import { QuoteSection } from "@/components/sections/QuoteSection";
import { FinalCta } from "@/components/sections/FinalCta";

const SEGMENTS = [
  { t: "Immobilienverwaltungen", d: "Treppenhäuser, Eingangsbereiche, Waschküchen und Aussenanlagen in festen Intervallen. Ein Plan pro Objekt, ein Ansprechpartner für alle." },
  { t: "Büros & Praxen", d: "Reinigung ausserhalb der Geschäftszeiten, Hygienestandards für medizinische Räume, diskretes Personal." },
  { t: "Retail & Gewerbe", d: "Verkaufsflächen, Schaufenster, Lager und Sanitäranlagen. Angepasst an Öffnungszeiten und Kundenfrequenz." },
  { t: "Eigentümer & Bauherren", d: "Bau- und Endreinigungen für Neubau, Umbau und Renovation. Koordiniert mit Bauleitung und Übergabetermin." },
];

const PRINCIPLES = [
  ["Fester Ansprechpartner", "Eine Person kennt Ihre Objekte, Ihre Anforderungen und Ihre Termine."],
  ["Dokumentierte Leistungen", "Was gereinigt wurde, ist nachvollziehbar. Auf Wunsch mit Reporting."],
  ["Planbare Kosten", "Feste Preise pro Einsatz oder Monat. Keine Überraschungen in der Abrechnung."],
  ["Skalierbar", "Ein Objekt oder ein ganzes Portfolio. Der Ablauf bleibt derselbe."],
];

export default function Geschaeftskunden() {
  return (
    <>
      <SEO title="Geschäftskunden" description="Reinigung für Unternehmen, Immobilienverwaltungen, Büros, Praxen und Gewerbe. Feste Ansprechpartner, klare Abläufe, planbare Kosten." path="/geschaeftskunden" />
      <PageHero label="Geschäftskunden" title={["Professionelle Räume", "brauchen professionelle", "Pflege."]} image={IMG.business} imageAlt="Modernes Büro mit Tageslicht" lead="Für Unternehmen, Verwaltungen und Eigentümer, die ihre Objekte langfristig gepflegt wissen wollen. Ohne Aufwand auf Ihrer Seite.">
        <Button to="/kontakt?typ=business" variant="light" data-testid="b2b-hero-cta">B2B Anfrage</Button>
      </PageHero>
      <Marquee items={["Verwaltungen", "Büros", "Praxen", "Retail", "Gewerbe", "Liegenschaften"]} />

      <section className="bg-paper py-24 md:py-36">
        <div className="container-x">
          <MaskedLines as="h2" lines={["Für wen wir", "arbeiten."]} className="display text-4xl sm:text-5xl lg:text-6xl text-ink" />
          <ol className="mt-16 grid md:grid-cols-2 border-t border-l border-line">
            {SEGMENTS.map((s, i) => (
              <Reveal as="li" key={s.t} delay={i * 0.06} className="border-b border-r border-line p-8 md:p-12 hover:bg-white transition-colors duration-700 group">
                <span className="label text-mute group-hover:text-signal transition-colors">0{i + 1}</span>
                <h3 className="display text-2xl md:text-3xl text-ink mt-4">{s.t}</h3>
                <p className="text-sm md:text-base text-mute mt-4 leading-relaxed max-w-md">{s.d}</p>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      <section className="relative bg-signal text-white grain py-24 md:py-36">
        <div className="container-x relative z-10 grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <MaskedLines as="h2" lines={["Klare Abläufe.", "Saubere Ergebnisse."]} className="display text-4xl sm:text-5xl lg:text-6xl" />
            <Reveal delay={0.2} className="mt-10">
              <ImageReveal src={IMG.b2b} alt="Glasfassade eines Geschäftsgebäudes" className="aspect-[4/5] max-w-md" />
            </Reveal>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 lg:pt-20">
            <ul className="border-t border-white/25">
              {PRINCIPLES.map(([t, d], i) => (
                <Reveal as="li" key={t} delay={i * 0.06} className="border-b border-white/25 py-7 grid grid-cols-[3rem_1fr] gap-4">
                  <span className="label text-white/60 pt-1">0{i + 1}</span>
                  <span>
                    <span className="display text-2xl md:text-3xl block">{t}</span>
                    <span className="text-sm md:text-base text-white/80 leading-relaxed block mt-3 max-w-md">{d}</span>
                  </span>
                </Reveal>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <Process />
      <FAQ category="business" title="Fragen von Geschäftskunden." />
      <QuoteSection preset={{ object_type: "Büro", cleaning_types: ["Unterhaltsreinigung"] }} />
      <FinalCta />
    </>
  );
}
